import { useState } from "react";
import { Nav, Navbar } from "react-bootstrap";
import { Activity, CurrencyExchange, QuestionCircle } from "react-bootstrap-icons";
import Accordion from "react-bootstrap/Accordion";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { ReactSVG } from "react-svg";
import congregationIcon from "../../assets/congregation.svg";
import smpwIcon from "../../assets/smpw.svg";
import reportsIcon from "../../assets/graph.svg";
import logo from "../../assets/hourglass-logo-brown.svg";
import schedulingIcon from "../../assets/meeting.svg";
import publishersIcon from "../../assets/publishers.svg";
import HourglassGlobals from "../../helpers/globals";
import { Permission } from "../../types/permission";
import { NavCongregation } from "./congregation";
import { NavPublishers } from "./persons";
import { NavReports } from "./reports";
import { NavScheduling } from "./scheduling";
import { UserStatus } from "../../types/user";
import { hasPermission } from "../../helpers/hasPermission";
import { NavSMPW } from "./smpw";

function canViewSchedulesMenu(): boolean {
  return (
    hasPermission(Permission.ViewSchedules) ||
    hasPermission(Permission.UpdateMaintenanceSchedules) ||
    hasPermission(Permission.UpdateCustomAssignments) ||
    hasPermission(Permission.ViewWeekendSpeakers)
  );
}

function canViewSMPWMenu(): boolean {
  return !!HourglassGlobals.authUser.smpw_root_zone_id;
}

function canViewCongMenus(): boolean {
  if (hasPermission(Permission.ViewWeekendSpeakers) && HourglassGlobals.permissions.size === 1) return false;
  return !!HourglassGlobals.cong;
}

function isCO(): boolean {
  return HourglassGlobals.authUser.status === UserStatus.CO;
}

//this is the sidebar navigation for desktop/larger screens
function MainNav() {
  const { t } = useTranslation();

  return (
    <div className="d-flex flex-column side-section">
      <Link to="/">
        <div className="brand-label overflow-hidden p-2 d-none d-md-block">
          <img src={logo} className="top-nav-logo" alt="Hourglass logo" />
        </div>
      </Link>
      <div className="d-none d-md-flex d-print-none" id="nav-wrap">
        <Accordion flush className="nav" id="mainNav">
          {canViewCongMenus() && (
            <Accordion.Item eventKey="publishers" id="publishers-nav">
              <Accordion.Header className="nav-link">
                <ReactSVG src={publishersIcon} />
                <span className="nav-max-width-title">{t("general.persons")}</span>
              </Accordion.Header>
              <Accordion.Body>
                <NavPublishers />
              </Accordion.Body>
            </Accordion.Item>
          )}

          {hasPermission(Permission.ViewSummary) && canViewCongMenus() && (
            <Accordion.Item eventKey="reports" id="reports-nav">
              <Accordion.Header className="nav-link">
                <ReactSVG src={reportsIcon} />
                {t("nav.reports.0")}
              </Accordion.Header>
              <Accordion.Body>
                <NavReports />
              </Accordion.Body>
            </Accordion.Item>
          )}

          {canViewSchedulesMenu() && (
            <Accordion.Item eventKey="scheduling" id="scheduling-nav">
              <Accordion.Header className="nav-link">
                <ReactSVG src={schedulingIcon} />
                {t("schedules.scheduling")}
              </Accordion.Header>
              <Accordion.Body>
                <NavScheduling />
              </Accordion.Body>
            </Accordion.Item>
          )}

          {canViewCongMenus() && (
            <Accordion.Item eventKey="congregation" id="cong-nav">
              <Accordion.Header className="nav-link">
                <ReactSVG src={congregationIcon} />
                {t("list.congregation.title")}
              </Accordion.Header>
              <Accordion.Body>
                <NavCongregation />
              </Accordion.Body>
            </Accordion.Item>
          )}

          {canViewSMPWMenu() && (
            <Accordion.Item eventKey="smpw" id="smpw-nav">
              <Accordion.Header className="nav-link">
                <ReactSVG src={smpwIcon} />
                {t("smpw.smpw")}
              </Accordion.Header>
              <Accordion.Body>
                <NavSMPW />
              </Accordion.Body>
            </Accordion.Item>
          )}

          {isCO() && (
            <Link to="/circuit">
              <Accordion.Item eventKey="circuit">
                <Accordion.Header className="nav-link no-arrow">
                  <Activity className="me-2" />
                  {t("circuit.circuit")}
                </Accordion.Header>
              </Accordion.Item>
            </Link>
          )}

          {canViewCongMenus() && (
            <Link to="/docs">
              <Accordion.Item eventKey="docs">
                <Accordion.Header className="nav-link no-arrow">
                  <QuestionCircle className="me-2" />
                  {t("nav.help")}
                </Accordion.Header>
              </Accordion.Item>
            </Link>
          )}

          <a href="https://www.paypal.com/donate/?hosted_button_id=LEWREP6SGSV98" target="_blank" rel="noreferrer">
            <Accordion.Item eventKey="donate">
              <Accordion.Header className="nav-link no-arrow">
                <CurrencyExchange className="me-2" />
                {t("www.donate-button")}
              </Accordion.Header>
            </Accordion.Item>
          </a>
        </Accordion>
      </div>
    </div>
  );
}

export default MainNav;

enum MobileNavKeys {
  None,
  Publishers,
  Reports,
  Scheduling,
  Congregation,
  Help,
  Donate,
  SMPW,
  Circuit,
}

//mobile nav is the bottom bar, which has specific shortcut items
export function MobileNav() {
  const { t } = useTranslation();
  const loc = useLocation();
  const [active, setActive] = useState(MobileNavKeys.None);
  if (loc.pathname.startsWith("/nav/publishers") && active !== MobileNavKeys.Publishers) {
    setActive(MobileNavKeys.Publishers);
  } else if (loc.pathname.startsWith("/user/") && active !== MobileNavKeys.Publishers) {
    setActive(MobileNavKeys.Publishers);
  } else if (loc.pathname.startsWith("/docs") && active !== MobileNavKeys.Help) {
    setActive(MobileNavKeys.Help);
  } else if (loc.pathname === "/" && active !== MobileNavKeys.None) setActive(MobileNavKeys.None);

  return (
    <Navbar fixed="bottom" id="bottomNavbar" className="d-block d-md-none d-print-none" style={{ overflowX: "hidden" }}>
      <div className="scroll-indicator-mobile"></div>
      <Nav
        activeKey={active}
        className="flex-nowrap overflow-auto"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
      >
        {canViewCongMenus() && (
          <Nav.Item style={{ minWidth: "80px", flex: "0 0 auto" }}>
            <Nav.Link
              as={Link}
              to="/nav/publishers"
              className="nav-link"
              eventKey={MobileNavKeys.Publishers}
              onClick={() => setActive(MobileNavKeys.Publishers)}
            >
              <figure className="bottom-nav">
                <ReactSVG src={publishersIcon} />
                <figcaption>{t("general.persons")}</figcaption>
              </figure>
            </Nav.Link>
          </Nav.Item>
        )}
        {hasPermission(Permission.ViewSummary) && canViewCongMenus() && (
          <Nav.Item style={{ minWidth: "80px", flex: "0 0 auto" }}>
            <Nav.Link
              as={Link}
              to="/nav/reports"
              className="nav-link"
              eventKey={MobileNavKeys.Reports}
              onClick={() => setActive(MobileNavKeys.Reports)}
            >
              <figure className="bottom-nav">
                <ReactSVG src={reportsIcon} />
                <figcaption>{t("nav.reports.0")}</figcaption>
              </figure>
            </Nav.Link>
          </Nav.Item>
        )}
        {canViewSchedulesMenu() && (
          <Nav.Item style={{ minWidth: "80px", flex: "0 0 auto" }}>
            <Nav.Link
              as={Link}
              to="/nav/scheduling"
              className="nav-link"
              eventKey={MobileNavKeys.Scheduling}
              onClick={() => setActive(MobileNavKeys.Scheduling)}
            >
              <figure className="bottom-nav">
                <ReactSVG src={schedulingIcon} />
                <figcaption>{t("schedules.scheduling")}</figcaption>
              </figure>
            </Nav.Link>
          </Nav.Item>
        )}
        {canViewCongMenus() && (
          <Nav.Item style={{ minWidth: "80px", flex: "0 0 auto" }}>
            <Nav.Link
              as={Link}
              to="/nav/congregation"
              className="nav-link"
              eventKey={MobileNavKeys.Congregation}
              onClick={() => setActive(MobileNavKeys.Congregation)}
            >
              <figure className="bottom-nav">
                <ReactSVG src={congregationIcon} />
                <figcaption>{t("list.congregation.title")}</figcaption>
              </figure>
            </Nav.Link>
          </Nav.Item>
        )}
        {canViewSMPWMenu() && (
          <Nav.Item style={{ minWidth: "80px", flex: "0 0 auto" }}>
            <Nav.Link
              as={Link}
              to="/nav/smpw"
              className="nav-link"
              eventKey={MobileNavKeys.SMPW}
              onClick={() => setActive(MobileNavKeys.SMPW)}
            >
              <figure className="bottom-nav">
                <ReactSVG src={smpwIcon} />
                <figcaption>{t("smpw.smpw")}</figcaption>
              </figure>
            </Nav.Link>
          </Nav.Item>
        )}
        {canViewCongMenus() && (
          <Nav.Item style={{ minWidth: "80px", flex: "0 0 auto" }}>
            <Nav.Link
              as={Link}
              to="/docs/"
              className="nav-link"
              eventKey={MobileNavKeys.Help}
              onClick={() => setActive(MobileNavKeys.Help)}
            >
              <figure className="bottom-nav">
                <QuestionCircle />
                <figcaption>{t("nav.help")}</figcaption>
              </figure>
            </Nav.Link>
          </Nav.Item>
        )}
        {isCO() && (
          <Nav.Item style={{ minWidth: "80px", flex: "0 0 auto" }}>
            <Nav.Link
              as={Link}
              to="/circuit"
              className="nav-link"
              eventKey={MobileNavKeys.Circuit}
              onClick={() => setActive(MobileNavKeys.Circuit)}
            >
              <figure className="bottom-nav">
                <Activity className="me-2" />
                <figcaption>{t("circuit.circuit")}</figcaption>
              </figure>
            </Nav.Link>
          </Nav.Item>
        )}
      </Nav>
    </Navbar>
  );
}
